import React from 'react';
import PropTypes from 'prop-types';

import { Modal, Button, RadioLabel, Table } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import Style from './modalAction.module.css';
import useAppContext from '../../../../../context/useAppContext';
import LoadingCard from '../../../../../components/loadingCard';
import OrderModel from './../../../../order/models/orderModel';
import { AttendeeStatus } from '../../../list/components/attendee-row/attendee-row';

function ContentOrder({ order, attendees }) {
    const { t } = useTranslation();

    if (!order || !attendees) {
        return null;
    }

    return (
        <div className={Style['content-order']}>
            <div className={Style['content-order__left']}>
                <div className={Style['content-order__left-order']}>
                    {t('ticket.manager.modalAction.orderNumber')}
                    {order.transaction_reference}
                </div>
                <div className={Style['content-order__left-name']}>{order.resolved_buyer_name}</div>
                {order.buyer_email.length > 0 && <div className={Style['content-order__left-mail']}>{order.buyer_email}</div>}
            </div>
            <div className={Style['content-order__right']}>{t('ticket.manager.modalAction.countSubmit', { count: order.attendee_count })}</div>
        </div>
    );
}

ContentOrder.propTypes = {
    attendees: PropTypes.array,
    order: PropTypes.object
};

function ContentAttendees({ actionInstance, attendees, selectedAttendees, order, handleClick, handleAllClick }) {
    const { t } = useTranslation();

    if (!attendees) {
        return null;
    }

    return (
        <div>
            <Table useNew selectable onSelectAll={(event, data) => handleAllClick({ event, data })}>
                <Table.THead>
                    <Table.Tr isHeader active={Object.values(selectedAttendees).length === attendees.length} className={Style['tr-header']}>
                        <th>{t('ticket.manager.modalAction.selectAll')}</th>
                    </Table.Tr>
                </Table.THead>
                <Table.TBody>
                    {attendees.map((attendee, index) => {
                        let is_valid = actionInstance.constructor.is_attendee_valid({ attendee, order });
                        return (
                            <Table.Tr
                                disabled={!is_valid}
                                key={attendee.id}
                                index={index}
                                active={is_valid && Boolean(selectedAttendees[attendee.id])}
                                onSelected={(event, data) => handleClick({ event, data }, attendee)}
                            >
                                <Table.Td>
                                    <div className={Style['content-attendees__content-td']}>
                                        <div className={Style['content-attendees__name-ticket']}>
                                            <span className={Style['content-attendees__name']}>{attendee.resolved_attendee_name}</span>
                                            <span className={Style['content-attendees__ticket']}>{attendee.id_weez_ticket}</span>
                                        </div>
                                        <AttendeeStatus className={Style['content-attendees__status']} attendee={attendee} order={order} />
                                    </div>
                                </Table.Td>
                            </Table.Tr>
                        );
                    })}
                </Table.TBody>
            </Table>
        </div>
    );
}

ContentAttendees.propTypes = {
    attendees: PropTypes.array,
    order: PropTypes.object,
    handleClick: PropTypes.func,
    handleAllClick: PropTypes.func,
    selectedAttendees: PropTypes.object
};

export default function ModalAction({
    actionInstance = null,
    onClose = () => {},
    order,
    onOrderSelected = () => {},
    onAttendeesSelected = () => {},
    orderMode = true,
    selectedAttendee = null
}) {
    const { t } = useTranslation();
    const [{ event, organization }] = useAppContext();

    const [_orderMode, setOrderMode] = React.useState(orderMode);
    const [attendees, setAttendees] = React.useState([]);
    const [selectedAttendees, setSelectedAttendees] = React.useState({});
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const [_order, loading, sync, error] = OrderModel.useApiModel(// eslint-disable-line
        {
            id: order?.id_transaction,
            eventId: event.pk,
            organizationId: organization.pk,
            launch: Boolean(actionInstance),
            onSuccess: order => setAttendees(order.attendees)
        },
        [order, actionInstance]
    );

    React.useEffect(() => {
        if (selectedAttendee) {
            setSelectedAttendees({ [selectedAttendee.id]: selectedAttendee });
        }
    }, [selectedAttendee]);

    const handleElementsList = React.useCallback(
        ({ event, data: { checked } }, attendee) => {
            event.stopPropagation();
            setSelectedAttendees(currentState => {
                const newSelected = { ...currentState };
                const isSelected = attendee.id in currentState;
                if (!checked && isSelected) {
                    delete newSelected[attendee.id];
                }

                if (checked && !isSelected) {
                    if (actionInstance.constructor.is_attendee_valid({ attendee, order: _order })) {
                        newSelected[attendee.id] = attendee;
                    }
                }

                return newSelected;
            });
        },
        [actionInstance, _order]
    );

    const handleAllClick = React.useCallback(
        ({ event, data: { checked } }) => {
            if (checked) {
                let attendeesTmp = {};
                attendees.forEach(attendee => {
                    if (actionInstance.constructor.is_attendee_valid({ attendee, order: _order })) {
                        attendeesTmp[attendee.id] = attendee;
                    }
                });
                setSelectedAttendees(attendeesTmp);
            } else {
                setSelectedAttendees({});
            }
        },
        [attendees, actionInstance, _order]
    );

    const handleSubmit = React.useCallback(() => {
        setIsSubmitting(true);
        if (_orderMode) {
            onOrderSelected(_order);
        } else {
            onAttendeesSelected(selectedAttendees);
        }
    }, [_orderMode, onOrderSelected, onAttendeesSelected, _order, selectedAttendees]);

    const _onClose = React.useCallback(() => {
        onClose();
        setIsSubmitting(false);
    }, [onClose]);

    const titleAction = React.useMemo(() => {
        if (!actionInstance) return '';
        if (actionInstance.constructor.NAME.includes('refund')) return t('ticket.common.action.validate');
        const generic_actions = ['send', 'restore', 'delete', 'download'];
        const found_action = generic_actions.find(act => actionInstance.constructor.NAME.includes(act));
        if (found_action) return t(`ticket.common.action.${found_action}`);
        return t('ticket.manager.modalAction.title.' + actionInstance.constructor.NAME);
    }, [actionInstance, t]);

    return (
        <Modal
            className={Style['modal-action']}
            size="large"
            open={Boolean(actionInstance)}
            onClose={_onClose}
            title={titleAction}
            submitButton={
                <Button
                    primary
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    isLoading={isSubmitting}
                    label={`${titleAction}
                                ${_orderMode ? '' : ' (' + t('ticket.manager.modalAction.countSubmit', { count: Object.values(selectedAttendees).length }) + ')'}`}
                />
            }
            cancelButton={<Button onClick={_onClose} label={t(`ticket.common.action.cancel`)} />}
            subtitle={actionInstance?.constructor.MODAL_SUBTITLE ? t(actionInstance.constructor.MODAL_SUBTITLE) : null}
        >
            <LoadingCard loading={loading} marginTop={'20px'}>
                <div>
                    <RadioLabel
                        className={Style['radio-label-custom']}
                        inline
                        bordered
                        name="orders"
                        checked={_orderMode}
                        label={actionInstance ? t('ticket.manager.modalAction.radio.order.' + actionInstance.constructor.NAME) : ''}
                        onClick={() => {
                            setOrderMode(true);
                        }}
                    />
                    <RadioLabel
                        className={Style['radio-label-custom']}
                        inline
                        bordered
                        name="attendees"
                        checked={!_orderMode}
                        label={actionInstance ? t('ticket.manager.modalAction.radio.attendees.' + actionInstance.constructor.NAME) : ''}
                        onClick={() => {
                            setOrderMode(false);
                        }}
                    />
                </div>
                <div>
                    {_orderMode ? (
                        <ContentOrder order={_order} attendees={attendees} />
                    ) : (
                        <ContentAttendees
                            actionInstance={actionInstance}
                            attendees={attendees}
                            selectedAttendees={selectedAttendees}
                            order={_order}
                            handleClick={handleElementsList}
                            handleAllClick={handleAllClick}
                        />
                    )}
                </div>
            </LoadingCard>
        </Modal>
    );
}

ModalAction.propTypes = {
    actionInstance: PropTypes.object,
    onClose: PropTypes.func,
    order: PropTypes.object,
    onOrderSelected: PropTypes.func,
    onAttendeesSelected: PropTypes.func,
    orderMode: PropTypes.bool,
    selectedAttendee: PropTypes.object
};
